import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ArrowUpRight, ArrowDownLeft, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { policyAreaLabel } from "./shared";
import { UpgradeNudge, LockedRow } from "@/components/entitlements/LockedPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = new Map();

const useCosponsorship = (legislatorId) => {
  const [data, setData] = useState(cache.get(legislatorId) || null);
  const [failed, setFailed] = useState(false);
  const timer = useRef(null);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (cache.has(legislatorId)) { setData(cache.get(legislatorId)); return undefined; }
    setData(null);
    const poll = async (attempt) => {
      try {
        const r = await axios.get(`${API}/legislators/${legislatorId}/cosponsorship`, { validateStatus: (s) => s === 200 || s === 202 });
        if (cancelled) return;
        if (r.status === 202) { if (attempt < 24) timer.current = setTimeout(() => poll(attempt + 1), 5000); else setFailed(true); return; }
        cache.set(legislatorId, r.data);
        setData(r.data);
      } catch { if (!cancelled) setFailed(true); }
    };
    poll(0);
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [legislatorId]);
  return { data, failed };
};

const partyClass = (p) => (p === "D" ? "bg-blue-100 text-blue-800" : p === "R" ? "bg-red-100 text-red-800" : "bg-purple-100 text-purple-800");

const MemberList = ({ rows, unit, testId }) => (
  <ul className="space-y-1.5" data-testid={testId}>
    {rows.map((r, i) => (
      <li key={r.bioguide || i} className="flex items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${partyClass(r.party)}`}>{r.party || "?"}{r.state ? `-${r.state}` : ""}</span>
          <Link to={`/legislator/${r.legislatorId}`} className="truncate font-medium text-gray-900 hover:text-blue-700 hover:underline" data-testid={`${testId}-link-${i}`}>{r.name}</Link>
        </div>
        <span className="shrink-0 font-mono font-bold text-gray-800" title={`${r.count} ${unit}`}>{r.count}</span>
      </li>
    ))}
  </ul>
);

const MonthBars = ({ data, color, label }) => (
  <div className="h-16" title={label}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <XAxis dataKey="month" hide />
        <Tooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} contentStyle={{ fontSize: 11, padding: "2px 6px" }} formatter={(v) => [v, label]} labelFormatter={(m) => new Date(`${m}-02`).toLocaleDateString(undefined, { month: "short", year: "numeric" })} />
        <Bar dataKey="count" fill={color} radius={[2, 2, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const Stat = ({ label, value, tip, testId, flag }) => (
  <div title={tip} data-testid={testId}>
    <p className="text-2xl font-black font-mono tabular-nums text-gray-900 leading-none flex items-center gap-1.5">
      {value}
      {flag && <span className="text-base text-orange-500 cursor-help select-none" title={flag} data-testid={`${testId}-flag`} aria-label={flag}>⚡</span>}
    </p>
    <p className="text-[11px] text-gray-500 mt-1">{label}</p>
  </div>
);

const Panel = ({ icon: Icon, title, subtitle, children, testId, tint }) => (
  <div className={`rounded-lg border p-4 ${tint}`} data-testid={testId}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-orange-500" />
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
    </div>
    <p className="text-xs text-gray-600 mb-4">{subtitle}</p>
    {children}
  </div>
);

export const CosponsorshipCard = ({ legislatorId, legislator }) => {
  const { data, failed } = useCosponsorship(legislatorId);
  const out = data?.outgoing;
  const inc = data?.incoming;
  const incTopBills = useMemo(() => (inc?.topBills || []).filter((b) => b.cosponsors > 0).slice(0, 3), [inc]);
  const lastName = (legislator?.name || "").split(" ").slice(-1)[0];
  const who = lastName ? `${legislator?.chamber === "Senate" ? "Sen." : "Rep."} ${lastName}` : "this member";
  const TrendIcon = inc?.trend === "rising" ? TrendingUp : inc?.trend === "falling" ? TrendingDown : Minus;
  const trendText = { rising: "Sign-ons are accelerating: more peers joined in the last 90 days than the 90 before.",
    falling: "Sign-ons are slowing: fewer peers joined in the last 90 days than the 90 before.",
    steady: "Sign-on pace is steady across the last two quarters.",
    insufficient: "Too few dated sign-ons yet to read a trend." }[inc?.trend] || "";
  return (
    <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="cosponsorship-card">
      <h2 className="text-2xl font-bold text-gray-900 mb-1" title="Who this member works with on paper: the bills they sign onto, and who signs onto theirs. 119th Congress only.">Co-Sponsorship Activity</h2>
      <p className="text-sm text-gray-600 mb-5">Outgoing shows whose bills {who} joins. Incoming shows who joins {who}'s bills, a peer-influence signal that rarely surfaces elsewhere.</p>
      {!data ? (
        <p className="text-sm text-gray-500" data-testid="cosponsorship-loading">{failed ? "Co-sponsorship records are still assembling. Check back in a minute." : "Pulling co-sponsorship records from Congress.gov…"}</p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <Panel icon={ArrowUpRight} title="Outgoing" subtitle={`Bills ${who} has joined as a co-sponsor`} testId="cosponsorship-outgoing" tint="border-blue-500/25 bg-blue-500/[0.06]">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Stat label="joined, 119th" value={out.totalCurrent} testId="outgoing-total" tip="Bills this member has co-sponsored in the 119th Congress, per Congress.gov." />
              <Stat label="per month" value={out.perMonth} testId="outgoing-rate" tip="Average bills joined per month since the 119th Congress convened." />
              <Stat label="cross-party" value={out.crossPartyShare == null ? ", " : `${out.crossPartyShare}%`} testId="outgoing-cross" tip={`Share of the ${out.resolvedBills} most recent joined bills whose lead sponsor is from the other party.`} />
            </div>
            {data.preview ? (
              <div className="space-y-1.5 mb-3" data-testid="outgoing-preview-locked">
                <LockedRow label="Bills joined per month" />
                <LockedRow label="Who initiates the bills they join" />
              </div>
            ) : (
              <>
                <MonthBars data={out.byMonth} color="#3b82f6" label="bills joined" />
                <p className="text-[10px] text-gray-500 mb-4">Bills joined per month, by introduction date</p>
                <p className="text-xs font-semibold text-gray-700 mb-1.5" title="Lead sponsors whose bills this member signs onto most often, from the most recent joined bills.">Who initiates the bills they join</p>
                {out.initiators.length ? <MemberList rows={out.initiators} unit="bills" testId="outgoing-initiators" /> : <p className="text-xs text-gray-500">No sponsor data resolved yet.</p>}
              </>
            )}
            {out.policyAreas.length > 0 && (
              <>
                <p className="text-xs font-semibold text-gray-700 mt-4 mb-1.5" title="Congress.gov policy areas of the bills this member co-sponsored.">Issues they join</p>
                <div className="flex flex-wrap gap-1.5" data-testid="outgoing-areas">
                  {out.policyAreas.map((a) => (
                    <span key={a.area} className="text-[11px] px-2 py-0.5 rounded-full border border-blue-200 bg-white text-blue-900">{policyAreaLabel(a.area)} <span className="font-mono text-blue-600">{a.count}</span></span>
                  ))}
                </div>
              </>
            )}
          </Panel>
          <Panel icon={ArrowDownLeft} title="Incoming" subtitle={`Peers who have signed onto ${who}'s own bills`} testId="cosponsorship-incoming" tint="border-orange-500/25 bg-orange-500/[0.06]">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <Stat label={`sign-ons on ${inc.billsSampled} bills`} value={inc.totalCosponsors} testId="incoming-total" tip={`Total co-sponsors across the ${inc.billsSampled} most recent bills this member sponsored in the 119th Congress.`} />
              <Stat label="avg per bill" value={inc.avgPerBill} testId="incoming-avg" flag={inc.peer?.flag ? inc.peer.text : null} tip={`Average number of co-sponsors each of their recent bills attracted.${inc.peer?.chamberMedian != null ? ` Chamber median is ${inc.peer.chamberMedian}.` : ""} Higher means peers line up behind their work.`} />
              <Stat label="cross-party" value={inc.crossPartyShare == null ? ", " : `${inc.crossPartyShare}%`} testId="incoming-cross" tip="Share of incoming sign-ons that came from the other party." />
            </div>
            {data.preview ? (
              <div className="space-y-1.5 mb-3" data-testid="incoming-preview-locked">
                <LockedRow label="Peer sign-ons per month" />
                <LockedRow label="Who signs on most often" />
                <LockedRow label="Their most-joined bills" />
              </div>
            ) : (
              <>
            <MonthBars data={inc.byMonth} color="#f97316" label="sign-ons" />
            <p className="text-[10px] text-gray-500 mb-2">Peer sign-ons per month, by sign-on date</p>
            <p className={`text-xs rounded border px-2 py-1.5 mb-4 flex items-center gap-1.5 ${inc.trend === "rising" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : inc.trend === "falling" ? "bg-rose-50 border-rose-200 text-rose-900" : "bg-gray-50 border-gray-200 text-gray-700"}`} data-testid="incoming-trend">
              <TrendIcon className="w-3.5 h-3.5 shrink-0" /> {trendText}
            </p>
            <p className="text-xs font-semibold text-gray-700 mb-1.5" title="Members who most frequently co-sponsor this member's bills.">Who signs on most often</p>
            {inc.recruiters.length ? <MemberList rows={inc.recruiters} unit="of their bills" testId="incoming-recruiters" /> : <p className="text-xs text-gray-500">None of their recent bills have attracted co-sponsors yet.</p>}
            {inc.topBills.length > 0 && inc.topBills[0].cosponsors > 0 && (
              <>
                <p className="text-xs font-semibold text-gray-700 mt-4 mb-1.5">Their most-joined bills</p>
                <ul className="space-y-1" data-testid="incoming-top-bills">
                  {incTopBills.map((b) => (
                    <li key={b.billId} className="text-xs flex items-start gap-2">
                      <span className="font-mono font-bold text-orange-700 shrink-0">{b.cosponsors}</span>
                      <Link to={`/bill/${b.billId}`} className="text-gray-800 hover:text-blue-700 hover:underline line-clamp-1">{b.title}</Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
              </>
            )}
          </Panel>
        </div>
      )}
      {data?.preview && (
        <UpgradeNudge feature="cosponsorship_activity" tier="pro" light className="mt-5"
          text="Preview: Pro members see who initiates the bills this member joins, who signs onto theirs, monthly trends and the peer-influence flag." />
      )}
    </Card>
  );
};
